import { useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UsageTypeChip } from '~/components/UsageTypeChip';
import { SeparatedDataGrid } from '~/components/data-grid';
import { DataGridPagination } from '~/components/data-grid/pagination';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { AuthorizedLink, pathFor } from '~/components/links/AuthorizedLink';
import { useAppContext } from '~/contexts';
import type { ContentOrderBy } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import AddAppsIllustration from '~/images/illustrations/add-apps.svg';
import {
  AppUsageDocument,
  type AppUsage__App as UsageApp,
  type AppUsage__Playlist as UsagePlaylist,
  type AppUsage__Show as UsageShow,
} from './queries/AppUsage.generated';

type UsageRow = UsageApp | UsagePlaylist | UsageShow;

const useColumns = (): GridColDef<UsageRow>[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'type',
      width: 96,
      headerName: t('common.labels.type'),
      resizable: false,
      sortable: false,
      renderCell: ({ row }) => <UsageTypeChip typename={row.__typename} />,
    },
    {
      field: 'name',
      flex: 2,
      minWidth: 140,
      headerName: t('common.labels.name'),
      resizable: true,
      renderCell: ({ row }) => <AuthorizedLink authorized={row.canUpdate.value} entity={row} />,
    },
  ];
};

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={AddAppsIllustration}
      header={t('pages.content.apps.usage.no_usage_header')}
      description={t('pages.content.apps.usage.no_usage_description')}
    />
  );
};

export const AppUsage = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const params = useParams<{ appId: string }>();
  const contentItemId = parseInt(params.appId ?? '');
  const navigate = useNavigate();
  const link = useLink();
  const { handleOutOfBounds, paginationModel, setPageParams } = usePaginationParams();
  const columns = useColumns();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const { orderBy, setOrderByParams, sortModel } = useOrderByParams<ContentOrderBy>();
  const { data, loading } = useQuery(AppUsageDocument, {
    variables: {
      networkId: currentNetwork.id,
      contentItemId,
      page: paginationModel.page + 1,
      perPage: paginationModel.pageSize,
      search,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (d) => handleOutOfBounds(d.network?.contentItem?.usages.pageInfo),
  });

  const rows = (data?.network?.contentItem?.usages.nodes ?? []).filter(
    (row): row is UsageRow => row.__typename !== 'Design',
  );
  const totalCount = useTotalCount(data?.network?.contentItem?.usages.pageInfo.nodesCount);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <SearchBar
          onChange={(value) =>
            setSearchParams((params) => {
              params.set('search', value);
              params.set('page', '1');
              return params;
            })
          }
          placeholder={t('pages.content.apps.usage.search_placeholder')}
          search={search}
        />
        <Typography variant="subtitle1">
          {t('pages.content.apps.usage.used_in_places_plural', { count: totalCount })}
        </Typography>
      </Box>
      <SeparatedDataGrid
        columns={columns}
        loading={loading}
        getRowId={(row) => `${row.__typename}-${row.id}`}
        onPaginationModelChange={(m) => setPageParams({ page: m.page + 1, perPage: m.pageSize })}
        onRowClick={({ row }: GridRowParams<UsageRow>) => navigate(link(pathFor(row)))}
        onSortModelChange={(m) => setOrderByParams(m.at(0) ?? null)}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={totalCount}
        rows={rows}
        slots={{
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
          pagination: DataGridPagination,
        }}
        sortingMode="server"
        sortModel={sortModel}
      />
    </>
  );
};
